import type { AppState, Commit, ComponentState, EcsAppState, EnvRef, EnvState, Health, KubeAppState, PodSummary, ProjectSnapshot, Termination } from "./model";

// Raw shapes, typed loosely on purpose: only the fields we read. They match what
// useProjectSnapshot collects and what __fixtures__/project.json records.
export interface RawStage {
    id: string;
    name: string;
    status: string;
    statusReason?: string | null;
    lastDeployed?: number;
    awsRegion?: string;
    cloudProvider?: string;
    /** Set by the project in environment settings; older backends don't send it */
    production?: boolean;
}

export interface RawResource {
    name: string;
    status: string;
    statusReason?: string | null;
    lastDeployed?: number | string;
    componentVersion?: string;
    configurations?: { key: string; value: string }[];
    component?: {
        id?: string;
        type?: string;
        version?: string;
        /** The template's name */
        name?: string;
        /** The template's JSON schema; its outputs say what the component exposes */
        schema?: { properties?: { outputs?: { properties?: Record<string, unknown> } } };
    };
}

export interface RawContainerStatus {
    name?: string;
    ready?: boolean;
    restartCount?: number;
    state?: { waiting?: { reason?: string }; running?: unknown; terminated?: { reason?: string } };
    lastState?: { terminated?: { reason?: string; exitCode?: number; finishedAt?: string } };
}

export interface RawPod {
    spec?: { containers?: { resources?: { limits?: { memory?: string }; requests?: { memory?: string } } }[] };
    status?: { phase?: string; containerStatuses?: RawContainerStatus[] };
}

export interface RawApp {
    name: string;
    kubernetesId: string;
    namespace: string;
    version?: string;
    lastDeployed?: number;
    clusterAvailable?: boolean;
    pods?: RawPod[];
    podStatus?: { totalPods?: number; runningPods?: number };
    usage?: { averageCpu?: string; averageMemory?: string };
}

export interface RawAppDeployment {
    name: string;
    kubernetesId?: string;
    timestamp?: string | number;
    commit?: {
        sha?: string;
        name?: string;
        type?: string;
        message?: string;
        repository?: string;
        user?: string | { name?: string };
    };
}

/** GET …/resources/{name}/ecs/status: the ECS service behind an ecs-app resource */
export interface RawEcsStatus {
    serviceArn?: string;
    url?: string;
    status?: string;
    runningCount?: number;
    desiredCount?: number;
    pendingCount?: number;
    imageTag?: string;
    cpu?: string | number;
    memory?: string | number;
    cpuUtilization?: number;
    memoryUtilization?: number;
    deployments?: { id: string; status?: string; rolloutState?: string; createdAt?: string | number; updatedAt?: string | number }[];
}

export type RawEcsRead = RawEcsStatus | { missing: true } | { error: string };

/** The pipeline behind an ecs-app resource's image, and the build its tag names when it was found */
export interface RawEcsBuild {
    pipelineId: string;
    /** The build, when found */
    buildId?: string;
    repository?: string;
    commit?: Commit;
    by?: string;
}

export interface RawEnv {
    details: { resources?: RawResource[]; statusReason?: string | null };
    kube: { id: string }[];
    deployed: { microservices: RawApp[] }[];
    appDeployments: Record<string, RawAppDeployment[] | { error: string }>;
    /**
     * ECS status per ecs-app resource, by resource name: undefined until asked, `missing` before
     * the first deploy created the service, an error when it couldn't be read
     */
    ecs?: Record<string, RawEcsRead>;
    /** By resource name, for the ecs-app resources whose pipeline is known */
    ecsBuilds?: Record<string, RawEcsBuild>;
}

export interface RawProject {
    stages: RawStage[];
    envs: Record<string, RawEnv>;
}

/**
 * How close an environment is to customers. A name heuristic until projects can
 * set their promotion order (planned backend setting).
 */
export function envImportance(name: string): number {
    const n = name.toLowerCase();
    if (/(^|[^a-z])(prod|production|live|prd)([^a-z]|$)/.test(n)) return 3;
    if (/stag|uat|pre-?prod|release/.test(n)) return 2;
    if (/qa|test|fasttrack|demo/.test(n)) return 1;
    return 0;
}

/** envImportance, with the environment's own production setting taking precedence over its name */
export function stageImportance(stage: { name: string; production?: boolean }): number {
    if (stage.production) return 3;
    const byName = envImportance(stage.name);
    return stage.production === false ? Math.min(byName, 2) : byName;
}

const CRASH_WAITING = ["CrashLoopBackOff", "ImagePullBackOff", "ErrImagePull", "CreateContainerConfigError", "CreateContainerError", "RunContainerError"];

export function parseMi(value?: string): number | undefined {
    if (!value) return undefined;
    const match = /^(\d+(?:\.\d+)?)(Ki|Mi|Gi|Ti|K|M|G|T)?$/.exec(value.trim());
    if (!match) return undefined;
    const bytesPerUnit: Record<string, number> = { "": 1, Ki: 2 ** 10, Mi: 2 ** 20, Gi: 2 ** 30, Ti: 2 ** 40, K: 1e3, M: 1e6, G: 1e9, T: 1e12 };
    return Math.round(parseFloat(match[1]) * bytesPerUnit[match[2] || ""] / 2 ** 20);
}

export function summarizePods(pods: RawPod[] = [], podStatus?: RawApp["podStatus"]): PodSummary {
    const summary: PodSummary = { total: 0, ready: 0, restarts: 0, oomKills: [] };
    for (const pod of pods) {
        const statuses = pod.status?.containerStatuses || [];
        summary.total += 1;
        if (statuses.length && statuses.every(s => s.ready)) summary.ready += 1;
        for (const status of statuses) {
            summary.restarts += status.restartCount || 0;
            const waiting = status.state?.waiting?.reason;
            if (waiting && CRASH_WAITING.includes(waiting)) summary.waitingReason = waiting;
            const terminated = status.lastState?.terminated;
            if (terminated?.reason) {
                const termination: Termination = { reason: terminated.reason, exitCode: terminated.exitCode, at: terminated.finishedAt };
                if (terminated.reason === "OOMKilled") summary.oomKills.push(termination);
                if (!summary.lastTermination || (termination.at || "") > (summary.lastTermination.at || "")) {
                    summary.lastTermination = termination;
                }
            }
        }
        const resources = pod.spec?.containers?.[0]?.resources;
        summary.memoryLimitMi ??= parseMi(resources?.limits?.memory);
        summary.memoryRequestMi ??= parseMi(resources?.requests?.memory);
    }
    // Some responses carry counts without pod objects
    if (!pods.length && podStatus?.totalPods) {
        summary.total = podStatus.totalPods;
        summary.ready = podStatus.runningPods || 0;
    }
    return summary;
}

export function appHealth(pods: PodSummary, clusterAvailable = true): Health {
    if (!clusterAvailable) return "unknown";
    if (pods.waitingReason) return "down";
    if (pods.total === 0) return "idle";
    if (pods.ready === 0) return "down";
    if (pods.ready < pods.total) return "degraded";
    return "healthy";
}

export function componentHealth(status: string): Health {
    if (/FAILED|ROLLBACK/.test(status)) return "down";
    if (/IN_PROGRESS|PENDING/.test(status)) return "unknown";
    if (/DELETE_COMPLETE|NOT_DEPLOYED/.test(status)) return "idle";
    return "healthy";
}

// Microtica's catalog components behind the Amazon ECS runtime: one ecs-runtime resource per
// environment, and an ecs-app resource per app
export const ECS_APP_COMPONENT = "ecs-app";
export const ECS_RUNTIME_COMPONENT = "ecs-runtime";
export const ECS_RUNTIME_RESOURCE = "EcsRuntime";

const slug = (value?: string) => (value || "").trim().toLowerCase().replace(/[\s_]+/g, "-");

/** Whether a resource's component is an ECS app or the ECS runtime; matched by id or name */
export function ecsKindOf(component?: { id?: string; name?: string }): "app" | "runtime" | undefined {
    // Public catalog ids are "microtica-aws-ecs-app"; names like "ECS App"
    const names = [slug(component?.id), slug(component?.id).replace(/^microtica-aws-/, ""), slug(component?.name)];
    if (names.includes(ECS_APP_COMPONENT)) return "app";
    if (names.includes(ECS_RUNTIME_COMPONENT)) return "runtime";
    return undefined;
}

/** How the app runtime reads wherever components are listed */
export const ECS_RUNTIME_LABEL = { name: "App runtime", detail: "Amazon ECS, by Microtica" };

/** A resource's saved value for one input */
export const configValue = (resource: Pick<RawResource, "configurations">, key: string) =>
    resource.configurations?.find(c => c.key === key)?.value || undefined;

/**
 * An ECS app's health: from its tasks once ECS has said, else unknown, unless the resource
 * was never deployed. A deploy that failed shows as a signal; old tasks may still serve.
 */
export function ecsHealth(status: string, tasks?: { running: number; desired: number }, missing = false): Health {
    if (/NOT_DEPLOYED|DELETE_COMPLETE/.test(status) || missing) return "idle";
    if (!tasks) return "unknown";
    if (tasks.desired === 0) return "idle";
    if (tasks.running === 0) return "down";
    if (tasks.running < tasks.desired) return "degraded";
    return "healthy";
}

const isSha = (value?: string) => !!value && /^[0-9a-f]{7,40}$/i.test(value);

/**
 * An ECS app's commit, as a cluster app's: the build its image tag names, else the tag itself when
 * it's a commit. The repository is its pipeline's, so the app groups with it even before the build is known.
 */
export function ecsCommit(version?: string, build?: RawEcsBuild): Commit | undefined {
    const c = build?.commit;
    // A tag that's the build's id isn't a commit, however much it looks like one
    const sha = c?.sha || (isSha(version) && build?.buildId !== version ? version! : "");
    if (!sha && !c && !build?.repository) return undefined;
    return {
        ...c,
        sha,
        short: sha.slice(0, 7),
        user: c?.user || build?.by,
        repository: c?.repository || build?.repository,
    };
}

export function normalizeEcsApp(env: EnvRef, resource: RawResource, raw?: RawEcsRead, build?: RawEcsBuild): EcsAppState {
    const status = raw && !("error" in raw) && !("missing" in raw) ? raw : undefined;
    const missing = /NOT_DEPLOYED|DELETE_COMPLETE/.test(resource.status) || (!!raw && "missing" in raw);
    const tasks = status && status.desiredCount !== undefined ? { running: status.runningCount || 0, desired: status.desiredCount } : undefined;
    const pods: PodSummary = { total: tasks?.desired || 0, ready: tasks?.running || 0, pending: status?.pendingCount, restarts: 0, oomKills: [] };
    const version = status?.imageTag || configValue(resource, "ImageTag");
    const text = (value?: string | number) => (value === undefined || value === null || value === "" ? undefined : String(value));
    return {
        kind: "app",
        runtime: "ecs",
        env,
        name: resource.name,
        resourceName: resource.name,
        componentVersion: resource.componentVersion || resource.component?.version,
        status: resource.status,
        statusReason: resource.statusReason,
        version,
        commit: ecsCommit(version, build),
        pipelineId: build?.pipelineId,
        deployedAt: toIso(resource.lastDeployed),
        url: status?.url,
        cpu: text(status?.cpu) || configValue(resource, "Cpu"),
        memory: text(status?.memory) || configValue(resource, "Memory"),
        utilization: status && (status.cpuUtilization !== undefined || status.memoryUtilization !== undefined)
            ? { cpu: status.cpuUtilization, memory: status.memoryUtilization }
            : undefined,
        rollouts: (status?.deployments || []).map(d => ({ id: d.id, status: d.status, rolloutState: d.rolloutState, createdAt: toIso(d.createdAt), updatedAt: toIso(d.updatedAt) })),
        statusKnown: !!status,
        notDeployed: missing,
        pods,
        health: ecsHealth(resource.status, tasks, missing),
    };
}

function toIso(value?: number | string): string | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    const date = new Date(typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * "Merge pull request #172 from org/branch" becomes PR 172 plus whatever follows.
 * Messages are stored without line breaks, so the PR title can run straight into
 * the branch name; it is dropped with the branch in that case.
 */
export function commitTitle(message?: string): { title?: string; pr?: string } {
    if (!message) return {};
    // Microtica appends the trigger to manual builds; the card already shows the branch
    const text = message.trim().replace(/\s*\(manual trigger on [^)]*\)\s*$/i, "");
    // Git (unlike Microtica's copy) keeps line breaks; the title is the first non-empty line
    const firstLine = (value: string) => value.split("\n").map(line => line.trim()).find(Boolean) || "";
    const pr = /^Merge pull request #(\d+) from (\S+)\s*(.*)$/s.exec(text);
    if (pr) return { pr: pr[1], title: firstLine(pr[3]) || pr[2] };
    const branch = /^Merge (?:remote-tracking )?branch ['‘’](?:origin\/)?([^'‘’]+)['‘’](?: into (\S+))?/.exec(text);
    if (branch) return { title: `Merge ${branch[1]}${branch[2] ? ` into ${branch[2]}` : ""}` };
    return { title: firstLine(text) };
}

function toCommit(deployment?: RawAppDeployment, fallbackSha?: string): Commit | undefined {
    const c = deployment?.commit;
    const sha = c?.sha || fallbackSha;
    if (!sha) return undefined;
    const user = typeof c?.user === "string" ? c.user : c?.user?.name;
    return {
        sha,
        short: sha.slice(0, 7),
        branch: c?.name,
        branchType: c?.type,
        message: c?.message,
        ...commitTitle(c?.message),
        user,
        at: toIso(deployment?.timestamp),
        repository: c?.repository,
    };
}

export function normalizeProject(raw: RawProject): ProjectSnapshot {
    const envs: EnvState[] = raw.stages.map(stage => {
        const rawEnv = raw.envs[stage.id];
        const env = { id: stage.id, name: stage.name };

        const deploymentsByApp = new Map<string, RawAppDeployment>();
        for (const [clusterId, list] of Object.entries(rawEnv?.appDeployments || {})) {
            if (!Array.isArray(list)) continue;
            for (const deployment of list) {
                deploymentsByApp.set(`${deployment.kubernetesId || clusterId}/${deployment.name}`, deployment);
            }
        }

        const kubeApps: KubeAppState[] = (rawEnv?.deployed || []).flatMap(group => group.microservices).map(app => {
            const pods = summarizePods(app.pods, app.podStatus);
            const deployment = deploymentsByApp.get(`${app.kubernetesId}/${app.name}`);
            return {
                kind: "app" as const,
                runtime: "kubernetes" as const,
                env,
                name: app.name,
                clusterId: app.kubernetesId,
                namespace: app.namespace,
                version: app.version,
                commit: toCommit(deployment, app.version),
                deployedAt: toIso(deployment?.timestamp) || toIso(app.lastDeployed),
                pods,
                usage: app.usage ? { cpu: app.usage.averageCpu, memory: app.usage.averageMemory } : undefined,
                health: appHealth(pods, app.clusterAvailable !== false),
            };
        });

        // ECS apps are resources of the environment, but they're apps to the people using it
        const resources = rawEnv?.details.resources || [];
        const ecsApps = resources.filter(r => ecsKindOf(r.component) === "app").map(r => normalizeEcsApp(env, r, rawEnv?.ecs?.[r.name], rawEnv?.ecsBuilds?.[r.name]));
        const apps: AppState[] = [...kubeApps, ...ecsApps];

        const components: ComponentState[] = resources.filter(r => ecsKindOf(r.component) !== "app").map(resource => ({
            kind: "component" as const,
            env,
            name: resource.name,
            type: resource.component?.type || "other",
            template: resource.component?.name,
            outputs: Object.keys(resource.component?.schema?.properties?.outputs?.properties || {}),
            status: resource.status,
            statusReason: resource.statusReason,
            version: resource.componentVersion || resource.component?.version,
            deployedAt: toIso(resource.lastDeployed),
            health: componentHealth(resource.status),
            ...(ecsKindOf(resource.component) === "runtime" && { appRuntime: "ecs" as const }),
        }));

        return {
            ...env,
            status: stage.status,
            statusReason: stage.statusReason ?? rawEnv?.details.statusReason,
            lastDeployed: toIso(stage.lastDeployed),
            region: stage.awsRegion,
            cloudProvider: stage.cloudProvider,
            importance: stageImportance(stage),
            apps,
            components,
            clusters: (rawEnv?.kube || []).map(k => k.id),
        };
    });

    return { envs };
}
